import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, ArrowUp, ArrowDown, MagicWand, ForkKnife, Fire, Camera } from "@phosphor-icons/react";

const EMPTY_STEP = () => ({ title: "", description: "", duration_min: 5, ingredients: [], photo_required: false });

function IngredientEditor({ value, onChange, materialOptions }) {
  const rows = value || [];
  const addRow = () => onChange([...rows, { material_id: "", name: "", qty: 25, unit: "g" }]);
  const setRow = (i, patch) => onChange(rows.map((r, k) => k === i ? { ...r, ...patch } : r));
  const del = (i) => onChange(rows.filter((_, k) => k !== i));

  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_80px_60px_28px] gap-1.5 items-center">
          <Select value={r.material_id || ""} onValueChange={(v) => {
            const m = materialOptions.find(m => m.id === v);
            setRow(i, { material_id: v, name: m?.name || r.name });
          }}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Ingredient"/></SelectTrigger>
            <SelectContent>
              {materialOptions.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="h-8 text-xs mono" type="number" step="0.1" value={r.qty}
                 onChange={e => setRow(i, { qty: parseFloat(e.target.value) || 0 })}/>
          <Select value={r.unit || "g"} onValueChange={v => setRow(i, { unit: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent>
              {["g","kg","ml","L","pc","tsp","tbsp"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
          <button className="text-red-600 hover:text-red-700" onClick={() => del(i)} title="Remove"><Trash size={14}/></button>
        </div>
      ))}
      <button onClick={addRow} className="text-[11px] text-primary font-semibold hover:underline">+ ingredient</button>
    </div>
  );
}

function StageEditor({ stageKey, steps, setSteps, materials }) {
  const Icon = stageKey === "preparation" ? ForkKnife : Fire;
  const total = steps.reduce((s, x) => s + (Number(x.duration_min) || 0), 0);

  const update = (i, patch) => setSteps(steps.map((s, k) => k === i ? { ...s, ...patch } : s));
  const del = (i) => setSteps(steps.filter((_, k) => k !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next);
  };
  const add = () => setSteps([...steps, EMPTY_STEP()]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={16}/>
          <div className="overline">{stageKey === "preparation" ? "Preparation" : "Cooking"} steps</div>
          <Badge variant="secondary">{steps.length} · ~{total.toFixed(0)} min</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={add} data-testid={`sop-add-${stageKey}`}>
          <Plus size={14} className="mr-1"/>Add step
        </Button>
      </div>

      {steps.length === 0 && (
        <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-4 text-center">
          No steps yet. Click <span className="font-semibold">Add step</span> or use <span className="font-semibold">Apply template</span> to start.
        </div>
      )}

      <div className="space-y-2">
        {steps.map((s, i) => (
          <div key={i} className="p-3 rounded-md border border-border bg-card" data-testid={`sop-editor-step-${stageKey}-${i+1}`}>
            <div className="flex items-start gap-2 mb-2">
              <span className="mono text-xs text-muted-foreground pt-1.5">#{i+1}</span>
              <div className="flex-1 space-y-1.5">
                <Input value={s.title} onChange={e => update(i, { title: e.target.value })}
                       placeholder="Step title (e.g. 'Temper mustard seeds')"
                       className="h-8 text-sm font-semibold" data-testid={`sop-title-${stageKey}-${i+1}`}/>
                <Textarea value={s.description} onChange={e => update(i, { description: e.target.value })}
                          placeholder="Method / description"
                          className="text-xs" rows={2}/>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground" title="Move up"><ArrowUp size={14}/></button>
                <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground" title="Move down"><ArrowDown size={14}/></button>
                <button onClick={() => del(i)} className="text-red-600 hover:text-red-700" title="Remove"><Trash size={14}/></button>
              </div>
            </div>
            <div className="grid grid-cols-[100px_1fr] gap-3">
              <div>
                <Label className="overline text-[9px]">Duration (min)</Label>
                <Input type="number" min="0" step="0.5" value={s.duration_min}
                       onChange={e => update(i, { duration_min: parseFloat(e.target.value) || 0 })}
                       className="h-8 text-sm mono"/>
              </div>
              <div>
                <Label className="overline text-[9px]">Ingredients used in this step</Label>
                <IngredientEditor value={s.ingredients || []} materialOptions={materials}
                                  onChange={(v) => update(i, { ingredients: v })}/>
              </div>
            </div>
            <label className="mt-2 flex items-center gap-2 text-[11px] cursor-pointer select-none"
                   data-testid={`sop-photo-req-label-${stageKey}-${i+1}`}>
              <input type="checkbox" checked={!!s.photo_required}
                     onChange={e => update(i, { photo_required: e.target.checked })}
                     data-testid={`sop-photo-req-${stageKey}-${i+1}`}/>
              <Camera size={13}/>
              <span className="font-semibold">Critical step — photo evidence required</span>
              <span className="text-muted-foreground">(chef must attach a photo to complete this step)</span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SopEditorDialog({ open, onOpenChange, sku }) {
  const [enforcement, setEnforcement] = useState("blocking");
  const [prep, setPrep] = useState([]);
  const [cook, setCook] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !sku) return;
    setLoading(true);
    Promise.all([
      api.get(`/skus/${sku.id}/sop`).then(r => r.data),
      api.get("/materials").then(r => r.data),
    ]).then(([s, m]) => {
      setEnforcement(s.enforcement || "blocking");
      setPrep(s.preparation || []);
      setCook(s.cooking || []);
      setMaterials(m);
    }).finally(() => setLoading(false));
  }, [open, sku]);

  const applyTemplate = async () => {
    if (!sku) return;
    if (!window.confirm("Apply the default SOP template? This overwrites current SOP.")) return;
    try {
      const { data } = await api.post(`/skus/${sku.id}/sop/apply-template`);
      setEnforcement(data.enforcement);
      setPrep(data.preparation);
      setCook(data.cooking);
      toast.success("Default template applied");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const save = async () => {
    if (!sku) return;
    setSaving(true);
    try {
      await api.put(`/skus/${sku.id}/sop`, {
        enforcement, preparation: prep, cooking: cook,
      });
      toast.success("SOP saved");
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>SOP · {sku?.name}</DialogTitle>
          <DialogDescription>
            Step-by-step guide chefs must follow. Blocking mode prevents advancing the batch until all steps are ticked.
          </DialogDescription>
        </DialogHeader>

        {loading ? <div className="p-6 text-muted-foreground">Loading SOP…</div> : (
          <>
            <div className="flex items-center justify-between border border-border rounded-md p-3 mb-2">
              <div>
                <Label className="overline text-[10px]">Enforcement</Label>
                <div className="mt-1">
                  <Select value={enforcement} onValueChange={setEnforcement}>
                    <SelectTrigger className="h-8 w-56 text-xs" data-testid="sop-enforcement">
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blocking">Blocking — must complete steps</SelectItem>
                      <SelectItem value="advisory">Advisory — track only, don't block</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={applyTemplate} data-testid="sop-apply-template">
                <MagicWand size={14} className="mr-1"/>Apply default template
              </Button>
            </div>

            <Tabs defaultValue="preparation" className="mt-2">
              <TabsList>
                <TabsTrigger value="preparation" data-testid="sop-tab-prep"><ForkKnife size={13} className="mr-1"/>Preparation ({prep.length})</TabsTrigger>
                <TabsTrigger value="cooking" data-testid="sop-tab-cook"><Fire size={13} className="mr-1"/>Cooking ({cook.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="preparation" className="mt-3">
                <StageEditor stageKey="preparation" steps={prep} setSteps={setPrep} materials={materials}/>
              </TabsContent>
              <TabsContent value="cooking" className="mt-3">
                <StageEditor stageKey="cooking" steps={cook} setSteps={setCook} materials={materials}/>
              </TabsContent>
            </Tabs>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="sop-save">
            {saving ? "Saving…" : "Save SOP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
